import { TextFieldProps, useForkRef } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
	DesktopDatePicker,
	DesktopDatePickerProps,
	LocalizationProvider,
	dateCalendarClasses,
	pickersDayClasses,
	pickersMonthClasses,
	pickersYearClasses,
	PickersDay,
	PickersDayProps,
	DateView,
} from "@mui/x-date-pickers";
import { CoreButton } from "../button/Button.tsx";
import { popperStyles, buttonStyles } from "./shared";
import CalendarIcon from "../images/calendarIcon.svg?react";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";
import { ScreenReaderText } from "../../screenReader/index.tsx";
import { ReactNode, useId, useRef, useState, forwardRef } from "react";

/**
 * To set the width of the component: set slotProps.textField.InputProps.sx.width
 */
export function CoreDatePicker({
	error,
	dayOfWeekFormatter = (date: Date) =>
		date.toString().charAt(0).toUpperCase() + date.toString().charAt(1).toLowerCase(),
	fixedWeekNumber = 6,
	showDaysOutsideCurrentMonth = true,
	monthsPerRow = 4,
	ariaLabel,
	ariaLabelledBy,
	dialogAriaLabel,
	dialogAriaLabelledBy,
	useButton,
	...props
}: {
	error?: ReactNode;
	/** The children will be under the DOM hierarchy of the parent component. @default false */
	disablePortal?: boolean;
	/**
	 * Formats the day of week displayed in the calendar header.
	 * @default
	 * ```(date: Date) => date.toString().charAt(0).toUpperCase() + date.toString().charAt(1).toLowerCase()```
	 */
	dayOfWeekFormatter?: DesktopDatePickerProps<Date>["dayOfWeekFormatter"];
	/**For textfield input */
	ariaLabel?: string;
	/**For textfield input */
	ariaLabelledBy?: string;
	dialogAriaLabel?: string;
	dialogAriaLabelledBy?: string;
	useButton?: boolean;
} & Omit<DesktopDatePickerProps<Date>, "dayOfWeekFormatter" | "slotProps"> & {
		slotProps?: Omit<Exclude<DesktopDatePickerProps<Date>["slotProps"], undefined>, "textField"> & {
			textField?: Partial<CoreTextFieldProps>;
		};
	}) {
	const [open, setOpen] = useState(false);
	const dialogId = useId();
	const [view, setView] = useState<DateView>("month");
	const popperRef = useRef<HTMLDivElement | null>(null);

	// Material UI DesktopDatePicker UP and DOWN arrow keys break ONLY for MONTH view and ONLY if monthsPerRow is 4.
	// You can test it here on Mui's official site: https://mui.com/x/react-date-pickers/date-picker/#views.
	// This function patches Up/Down arrow keys for month view and when monthsPerRow is 4.
	function handleMonthViewArrowKeys(event: React.KeyboardEvent<HTMLDivElement>) {
		if (view !== "month" || monthsPerRow !== 4 || !popperRef.current) return;

		const container = popperRef.current.querySelector(".MuiMonthCalendar-root");
		if (!container) return;

		const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('button[role="radio"]'));
		const active = document.activeElement as HTMLButtonElement | null;
		const currentIndex = buttons.indexOf(active!);
		if (currentIndex === -1) return;

		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			event.stopPropagation();

			const nextIndex =
				event.key === "ArrowDown"
					? (currentIndex + 4) % buttons.length
					: (currentIndex - 4 + buttons.length) % buttons.length;

			buttons[nextIndex]?.focus();
		}
	}

	return (
		<LocalizationProvider
			dateAdapter={AdapterDateFns}
			localeText={{
				fieldMonthPlaceholder: (_) => "MM",
			}}
		>
			<DesktopDatePicker
				{...props}
				className={error ? "CoreDatePicker-error" : ""}
				fixedWeekNumber={fixedWeekNumber}
				dayOfWeekFormatter={
					dayOfWeekFormatter ??
					((day: Date) => `${day.toString().charAt(0).toUpperCase()}${day.toString().charAt(1).toLowerCase()}`)
				}
				monthsPerRow={monthsPerRow}
				showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
				open={open}
				onOpen={() => {
					setOpen(true);
					props.onOpen?.();
				}}
				onClose={() => {
					setOpen(false);
					props.onClose?.();
				}}
				onViewChange={(view) => setView(view)}
				sx={{
					...props.sx,
				}}
				slotProps={{
					actionBar: {
						actions: [],
						hidden: true,
						...props.slotProps?.actionBar,
					},
					textField: {
						...(props.slotProps?.textField as TextFieldProps),
						error: error as never,
						sx: {
							fontVariantNumeric: "tabular-nums",
							margin: 0,
							...(props.slotProps?.textField && "sx" in props.slotProps.textField
								? props.slotProps.textField.sx
								: undefined),
						},
						inputProps: {
							...props.slotProps?.textField?.inputProps,
							role: "combobox",
							"aria-haspopup": "dialog",
							"aria-expanded": open,
							"aria-controls": dialogId,
							"aria-label": ariaLabel,
							"aria-labelledby": ariaLabelledBy,
						},
						onClick: (e) => {
							if (useButton) {
								setOpen(true);
							}
							props.slotProps?.textField?.onClick?.(e);
						},
					},
					inputAdornment: {
						...props.slotProps?.inputAdornment,
						sx: {
							margin: "0px",
							width: "24px",
							height: "24px",
							...props.slotProps?.inputAdornment?.sx,
						},
					},
					openPickerButton: {
						...props.slotProps?.openPickerButton,
						disableFocusRipple: true,
						sx: {
							"&:focus-visible": {
								outline: "2px solid var(--focus-outline-color-dark)",
							},
							"&&&&&": {
								padding: "0px",
								marginRight: "-3px", // Move icon a little to the left so focus outline has room to breathe
								height: "24px",
								width: "24px",
								borderRadius: "2px",
								// Centering the icon
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							},
							...(props.slotProps?.openPickerButton && "sx" in props.slotProps.openPickerButton
								? props.slotProps.openPickerButton.sx
								: undefined),
						},
					},
					openPickerIcon: {
						fontSize: "small",
						...props.slotProps?.openPickerIcon,
					},
					popper: {
						className: "CoreDatePicker-root",
						placement: "top-start",
						id: dialogId,
						"aria-modal": true,
						"aria-label": dialogAriaLabel ?? ariaLabel,
						"aria-labelledby": dialogAriaLabelledBy ?? ariaLabelledBy,
						ref: popperRef,
						onKeyDown: handleMonthViewArrowKeys,
						...props.slotProps?.popper,
						sx: {
							...popperStyles,
							[`& .${dateCalendarClasses.root}`]: {
								width: "278px",
								height: "308px",

								[`& .MuiDayCalendar-slideTransition`]: {
									minHeight: "218px",
								},

								// calendar header
								[`& .MuiPickersCalendarHeader-root`]: {
									padding: "0 12px",
									position: "relative",
									[`& .MuiPickersCalendarHeader-labelContainer`]: {
										width: "calc(100% - 88px)",
										margin: "0 32px",
										position: "absolute",
										justifyContent: "center",
										[`& .MuiPickersCalendarHeader-label`]: {
											fontWeight: "700",
											lineHeight: "30px",
											textAlign: "center",
											display: "flex",
											justifyContent: "center",
											height: "30px",
											padding: "4px 12px",
											borderRadius: "20px",
											margin: "0",
										},
										[`&:hover`]: {
											[`& .MuiPickersCalendarHeader-label`]: {
												backgroundColor: "#eceaf1",
											},
										},
										[`&:focus, &:focus-within`]: {
											[`& .MuiPickersCalendarHeader-label`]: {
												backgroundColor: "inherit",
												outline: "2px solid var(--focus-outline-color-dark)",
												outlineOffset: "-2px",
											},
											[`&:hover .MuiPickersCalendarHeader-label`]: {
												backgroundColor: "#e3dfe9",
											},
											[`&:active .MuiPickersCalendarHeader-label`]: {
												backgroundColor: "#c4bdd2",
											},
										},
										[`&:active`]: {
											[`& .MuiPickersCalendarHeader-label`]: {
												backgroundColor: "#e3dfe9",
											},
										},
										[`& .MuiPickersCalendarHeader-switchViewButton`]: {
											opacity: "0",
											width: "0",
											position: "0",
											margin: "0",
											padding: "0",
										},
									},
									[`& .MuiPickersArrowSwitcher-spacer`]: {
										width: "201px",
									},
									[`& .MuiPickersArrowSwitcher-button`]: {
										height: "37px",
										width: "37px",
										[`&:hover`]: {
											backgroundColor: "#eceaf1",
										},
										[`&:focus`]: {
											outline: "2px solid var(--focus-outline-color-dark)",
											backgroundColor: "inherit",
										},
										[`&:active`]: {
											backgroundColor: "#e3dfe9",
										},
										[`&:disabled:focus`]: {
											backgroundColor: "#e3dfe9",
										},
									},
								},

								[`& .MuiDayCalendar-weekDayLabel`]: {
									fontSize: "13px",
									height: "26px",
									width: "26px",
									margin: "9px 5px 3px",
								},
								// month and year calendar
								[`& .MuiMonthCalendar-root, & .MuiYearCalendar-root`]: {
									height: "244px",
									width: "90%",
									padding: "0",
									margin: "auto",
								},
								[`& .${pickersMonthClasses.root}`]: {
									height: "25%",
								},
								[`& .${pickersMonthClasses.monthButton}`]: {
									fontSize: "12px",
									height: "36px",
									width: "36px",
									margin: "5px",
									...buttonStyles,
								},
								[`& .${pickersYearClasses.yearButton}`]: {
									fontSize: "12px",
									height: "42px",
									width: "42px",
									borderRadius: "21px",
									margin: "5px",
									marginY: "19px",
									...buttonStyles,
								},
							},
							...(props.slotProps?.popper && "sx" in props.slotProps.popper ? props.slotProps.popper.sx : undefined),
						},
					},
				}}
				slots={{
					day: CustomDatePickersDay,
					openPickerIcon: CalendarIcon,
					textField: useButton ? DatePickerButton : CoreTextField,
				}}
				format={props.format ?? "MMM-dd-yyyy"}
			/>
		</LocalizationProvider>
	);
}

const CustomDatePickersDay = ({ disabled, sx, ...props }: PickersDayProps<Date>) => {
	return (
		<PickersDay
			{...props}
			disabled={props.outsideCurrentMonth || disabled}
			disableHighlightToday={false}
			sx={{
				[`&.${pickersDayClasses.root}`]: {
					fontSize: "12px",
					height: "26px",
					width: "26px",
					margin: "3px 5px",
					...buttonStyles,

					// override specific stylings in buttonStyles
					[`:hover`]: {
						backgroundColor: "#42296e",
						color: "white",
					},
				},
				[`&.${pickersDayClasses.dayOutsideMonth}`]: {
					opacity: 0.4,
				},
				[`&.${pickersDayClasses.today}`]: {
					border: "none",
					backgroundColor: "transparent",
				},
			}}
		/>
	);
};

const DatePickerButton = forwardRef<HTMLButtonElement, any>((props, ref) => {
	const {
		onClick,
		inputProps,
		InputProps,
		error,
		disabled,
		placeholder,
		onKeyDown,
		inputRef,
		value,
		sx,
		ownerState: _ownerState, // Destructure unused MUI props to prevent errors
		variant: _variant,
		color: _color,
		focused: _focused,
		label: _label,
		helperText: _helperText,
		fullWidth: _fullWidth,
		size: _size,
		...other
	} = props;

	const {
		"aria-haspopup": ariaHasPopup,
		"aria-expanded": ariaExpanded,
		"aria-controls": ariaControls,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		role: _role,
		...otherInputProps
	} = inputProps || {};

	const displayValue = value || inputProps?.value || placeholder || "MM/DD/YYYY";
	const handleRef = useForkRef(useForkRef(ref, inputRef), InputProps?.ref);
	const externalSx = Array.isArray(sx) ? Object.assign({}, ...sx) : sx;

	return (
		<CoreButton
			{...other}
			{...otherInputProps}
			ref={handleRef}
			endIcon={<CalendarIcon />}
			onClick={onClick}
			disabled={disabled}
			aria-haspopup={ariaHasPopup}
			aria-expanded={ariaExpanded}
			aria-controls={ariaControls}
			aria-labelledby={ariaLabelledBy}
			style={{
				justifyContent: "space-between",
				paddingRight: "10px",
				fontSize: "15px",
				textTransform: "none",
				border: "1px solid",
				borderColor: error ? "#bf0033" : "#6f6f6f",
				color: "#4d4d4d",
			}}
			sx={{
				width: "185px",
				height: "36px",
				"&&&:hover, &&&:active, &&&:focus-visible": {
					backgroundColor: "rgba(0, 0, 0, 0.04)",
					borderColor: error ? "#bf0033" : "#6f6f6f",
					color: "#4d4d4d",
				},
				"&&&& svg path": {
					fill: "#4d4d4d",
				},
				...externalSx,
			}}
		>
			{!ariaLabelledBy && ariaLabel && <ScreenReaderText>{ariaLabel}&nbsp;</ScreenReaderText>}
			{displayValue}
		</CoreButton>
	);
});
